import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import CostTierBadge from 'components/UI/CostTierBadge';
import { type FC, useMemo, useRef, useState } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
} from 'styles/tokens';
import { getModelGroups } from 'utils/modelGroups';

interface ModelPickerProps {
  value: string;
  onChange: (model: string) => void;
  sx?: SxProps<Theme>;
}

/** Searchable, provider-grouped model select. Search filters by model name + provider label. */
const ModelPicker: FC<ModelPickerProps> = ({ value, onChange, sx }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const filterRef = useRef<HTMLInputElement>(null);
  const groups = useMemo(() => getModelGroups(), []);

  const normalized = filter.trim().toLowerCase();
  const filteredGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          provider: group.provider,
          models: group.models.filter(
            (model) => model.toLowerCase().includes(normalized) || group.provider.toLowerCase().includes(normalized)
          ),
        }))
        .filter((group) => group.models.length > 0),
    [groups, normalized]
  );

  return (
    <Select
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
        setTimeout(() => filterRef.current?.focus(), 0);
      }}
      onClose={() => {
        setOpen(false);
        setFilter('');
      }}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      renderValue={(selected) => (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected}</span>
          <CostTierBadge model={selected} sx={{ fontSize: 12 }} />
        </span>
      )}
      MenuProps={{
        sx: {
          '& .MuiPaper-root': {
            mt: 0.5,
            maxHeight: 420,
            borderRadius: RADIUS_CARD,
            background: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            boxShadow: SHADOW,
          },
        },
      }}
      sx={{
        minWidth: 230,
        borderRadius: RADIUS_ITEM,
        bgcolor: SURFACE_CONTAINER_LOW,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12.5,
        color: NAVY,
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        ...sx,
      }}
    >
      <ListSubheader
        sx={{
          px: 1.5,
          pt: 1,
          pb: 1,
          // Solid (near-opaque) sticky header so scrolling rows don't show through the search field.
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: 'rgba(249, 250, 253, 0.97)',
          backdropFilter: 'blur(8px)',
          boxShadow: `0 1px 0 ${alpha(NAVY, 0.06)}`,
          lineHeight: 'unset',
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <InputBase
          inputRef={filterRef}
          placeholder="Buscar modelo o proveedor..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          fullWidth
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16, color: alpha(NAVY, 0.4) }} />
            </InputAdornment>
          }
          sx={{
            fontSize: 13,
            color: NAVY,
            bgcolor: alpha(NAVY, 0.04),
            borderRadius: '10px',
            px: 1,
            py: 0.5,
            '& input': { py: 0 },
            '& input::placeholder': { color: alpha(NAVY, 0.4), opacity: 1 },
          }}
        />
      </ListSubheader>
      {filteredGroups.flatMap((group) => [
        <ListSubheader
          key={`header-${group.provider}`}
          disableSticky
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: alpha(NAVY, 0.5),
            background: 'transparent',
            lineHeight: '30px',
          }}
        >
          {group.provider}
        </ListSubheader>,
        ...group.models.map((model) => (
          <MenuItem
            key={model}
            value={model}
            sx={{
              pl: 3,
              pr: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12.5,
              color: NAVY,
            }}
          >
            <span>{model}</span>
            <CostTierBadge model={model} sx={{ fontSize: 12.5 }} />
          </MenuItem>
        )),
      ])}
      {/* Keep the current value selectable even when filtered out, so the Select stays controlled.
          Guard with a boolean (not the `normalized` string): `'' && …` returns `''`, which React
          renders as a text child of <Select>, and MUI then reads `''.props.value` → crash. */}
      {normalized !== '' && !filteredGroups.some((g) => g.models.includes(value)) && (
        <MenuItem value={value} sx={{ display: 'none' }}>
          {value}
        </MenuItem>
      )}
    </Select>
  );
};

export default ModelPicker;
